import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploadField from "@/components/admin/ImageUploadField";
import PdfUploadField from "@/components/admin/PdfUploadField";
import { Spinner } from "@/components/common/Primitives";
import { contentService } from "@/services/contentService";
import { storageService } from "@/services/storageService";
import { normalizeSettings } from "@/lib/settings";
import { invalidateAbout, invalidateHomeSections, invalidateSiteContent } from "@/lib/invalidateQueries";

function Field({ label, value, onChange, textarea }) {
  const testId = `hs-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div>
      <label className="label-bcs">{label}</label>
      {textarea ? (
        <textarea
          className="input-bcs min-h-[90px]"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      ) : (
        <input
          className="input-bcs"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      )}
    </div>
  );
}

function SectionCard({ title, description, children, onSave, saving, saveTestId }) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-card p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="font-serif text-xl font-semibold text-ink">{title}</h2>
          {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
        </div>
        {onSave && (
          <button
            type="button"
            className="btn-primary shrink-0"
            onClick={onSave}
            disabled={saving}
            data-testid={saveTestId}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function ListItemCard({ index, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, reorderTestId, children }) {
  const showReorder = onMoveUp || onMoveDown;
  return (
    <div className="rounded-lg border border-gray-100 bg-cream/40 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Item {index + 1}</span>
        <div className="flex items-center gap-0.5">
          {showReorder && (
            <>
              <button
                type="button"
                onClick={onMoveUp}
                disabled={!canMoveUp}
                className="p-1.5 rounded-md text-ink-muted hover:text-maroon hover:bg-maroon-50 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                title="Move up"
                data-testid={reorderTestId ? `${reorderTestId}-up-${index}` : undefined}
              >
                <ChevronUp size={16} />
              </button>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={!canMoveDown}
                className="p-1.5 rounded-md text-ink-muted hover:text-maroon hover:bg-maroon-50 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                title="Move down"
                data-testid={reorderTestId ? `${reorderTestId}-down-${index}` : undefined}
              >
                <ChevronDown size={16} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 rounded-md text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Remove"
            data-testid={`about-remove-item-${index}`}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      {children}
    </div>
  );
}

function AddItemButton({ label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-maroon hover:text-maroon-dark transition-colors"
      data-testid={testId}
    >
      <Plus size={15} />
      {label}
    </button>
  );
}

export default function AdminHomeSectionsPage() {
  const [home, setHome] = useState(null);
  const [about, setAbout] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    Promise.all([
      contentService.getHomeSections(),
      contentService.getAbout(),
      contentService.getSettings(),
    ]).then(([h, a, s]) => {
      setHome(h);
      setAbout(a);
      setSettings(s);
    });
  }, []);

  if (!home || !about || !settings) return <Spinner className="py-24" />;

  const setHero = (k, v) => setHome({ ...home, hero: { ...home.hero, [k]: v } });
  const setMission = (k, v) => setHome({ ...home, mission: { ...home.mission, [k]: v } });
  const setCta = (k, v) => setHome({ ...home, cta: { ...home.cta, [k]: v } });

  const setAboutHero = (k, v) => setAbout({ ...about, hero: { ...about.hero, [k]: v } });
  const setAboutVision = (k, v) => setAbout({ ...about, vision: { ...about.vision, [k]: v } });
  const setAboutFounded = (k, v) => setAbout({ ...about, founded: { ...about.founded, [k]: v } });
  const setAboutGratitude = (k, v) => setAbout({ ...about, gratitude: { ...about.gratitude, [k]: v } });
  const setAboutCta = (k, v) => setAbout({ ...about, cta: { ...about.cta, [k]: v } });
  const setMilestonesSection = (k, v) =>
    setAbout({ ...about, milestones_section: { ...about.milestones_section, [k]: v } });
  const setProgrammesSection = (k, v) =>
    setAbout({ ...about, programmes_section: { ...about.programmes_section, [k]: v } });
  const setLeadershipSection = (k, v) =>
    setAbout({ ...about, leadership_section: { ...about.leadership_section, [k]: v } });
  const setFormerPresidentsSection = (k, v) =>
    setAbout({ ...about, former_presidents_section: { ...about.former_presidents_section, [k]: v } });

  const updateHeroStat = (i, field, value) => {
    const stats = [...(about.hero?.stats || [])];
    stats[i] = { ...stats[i], [field]: value };
    setAbout({ ...about, hero: { ...about.hero, stats } });
  };

  const addHeroStat = () =>
    setAbout({
      ...about,
      hero: { ...about.hero, stats: [...(about.hero?.stats || []), { label: "", value: "" }] },
    });

  const removeHeroStat = (i) => {
    const stats = [...(about.hero?.stats || [])];
    stats.splice(i, 1);
    setAbout({ ...about, hero: { ...about.hero, stats } });
  };

  const updateFounderMember = (i, value) => {
    const list = [...(about.founded?.founder_members || [])];
    list[i] = value;
    setAbout({ ...about, founded: { ...about.founded, founder_members: list } });
  };

  const addFounderMember = () =>
    setAbout({
      ...about,
      founded: {
        ...about.founded,
        founder_members: [...(about.founded?.founder_members || []), ""],
      },
    });

  const removeFounderMember = (i) => {
    const list = [...(about.founded?.founder_members || [])];
    list.splice(i, 1);
    setAbout({ ...about, founded: { ...about.founded, founder_members: list } });
  };

  const updateListItem = (listKey, i, field, value) => {
    const list = [...(about[listKey] || [])];
    list[i] = { ...list[i], [field]: value };
    setAbout({ ...about, [listKey]: list });
  };

  const addListItem = (listKey, emptyItem) =>
    setAbout({ ...about, [listKey]: [...(about[listKey] || []), emptyItem] });

  const removeListItem = (listKey, i) => {
    const list = [...(about[listKey] || [])];
    list.splice(i, 1);
    setAbout({ ...about, [listKey]: list });
  };

  const moveListItem = (listKey, index, delta) => {
    const list = [...(about[listKey] || [])];
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const [item] = list.splice(index, 1);
    list.splice(target, 0, item);
    setAbout({ ...about, [listKey]: list });
  };

  async function saveHomeSections(sectionLabel) {
    setSavingKey(sectionLabel);
    try {
      await contentService.saveContent("home_sections", home);
      await invalidateHomeSections();
      toast.success(`${sectionLabel} saved`);
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSavingKey(null);
    }
  }

  async function saveAboutSection(sectionLabel) {
    setSavingKey(sectionLabel);
    try {
      await contentService.saveContent("about", about);
      await invalidateAbout();
      toast.success(`${sectionLabel} saved`);
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSavingKey(null);
    }
  }

  async function saveSettingsSection(sectionLabel) {
    setSavingKey(sectionLabel);
    try {
      await contentService.saveContent("settings", normalizeSettings(settings));
      await invalidateSiteContent();
      toast.success(`${sectionLabel} saved`);
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSavingKey(null);
    }
  }

  async function saveAll() {
    setSavingKey("all");
    try {
      await Promise.all([
        contentService.saveContent("home_sections", home),
        contentService.saveContent("about", about),
        contentService.saveContent("settings", normalizeSettings(settings)),
        invalidateHomeSections(),
        invalidateAbout(),
        invalidateSiteContent(),
      ]);
      toast.success("All changes saved");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSavingKey(null);
    }
  }

  const isSaving = (key) => savingKey === key;
  const isSavingAny = savingKey !== null;

  return (
    <div data-testid="admin-home-sections-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-ink">Pages & Home Sections</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Edit the homepage hero, mission block, CTA, footer PDFs (AGM & annual accounts) and About page content.
          </p>
        </div>
        <button className="btn-primary" onClick={saveAll} disabled={isSavingAny} data-testid="save-home-sections-btn">
          {isSaving("all") ? "Saving…" : "Save all changes"}
        </button>
      </div>

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard
          title="Homepage — Hero"
          onSave={() => saveHomeSections("Homepage — Hero")}
          saving={isSaving("Homepage — Hero")}
          saveTestId="save-home-hero-btn"
        >
          <Field label="Eyebrow" value={home.hero.eyebrow} onChange={(v) => setHero("eyebrow", v)} />
          <Field label="Title (line 1)" value={home.hero.title_line1} onChange={(v) => setHero("title_line1", v)} />
          <Field label="Title accent (theme colour)" value={home.hero.title_accent} onChange={(v) => setHero("title_accent", v)} />
          <Field label="Intro paragraph" value={home.hero.body} onChange={(v) => setHero("body", v)} textarea />
          <ImageUploadField
            label="Hero image"
            value={home.hero.image_url || ""}
            nameHint="home-hero"
            onChange={(v) => setHero("image_url", v)}
            onUpload={(file, hint) => storageService.uploadMediaGalleryImage(file, hint)}
            folderLabel="media_gallery"
            testId="home-hero-image"
          />
        </SectionCard>

        <SectionCard
          title="Homepage — Mission & CTA"
          onSave={() => saveHomeSections("Homepage — Mission & CTA")}
          saving={isSaving("Homepage — Mission & CTA")}
          saveTestId="save-home-mission-btn"
        >
          <Field label="Mission title" value={home.mission.title} onChange={(v) => setMission("title", v)} />
          <Field label="Mission text" value={home.mission.body} onChange={(v) => setMission("body", v)} textarea />
          <Field label="CTA title" value={home.cta.title} onChange={(v) => setCta("title", v)} />
          <Field label="CTA text" value={home.cta.body} onChange={(v) => setCta("body", v)} textarea />
        </SectionCard>

        <SectionCard
          title="Footer — BCS AGM"
          description="Annual report PDF shown in the site footer. Upload replaces the previous file in Cloudflare R2."
          onSave={() => saveSettingsSection("Footer — BCS AGM")}
          saving={isSaving("Footer — BCS AGM")}
          saveTestId="save-agm-pdf-btn"
        >
          <PdfUploadField
            label="AGM report PDF"
            value={settings.agm_pdf_url || ""}
            nameHint="bcs-agm-report"
            onChange={(url) => setSettings({ ...settings, agm_pdf_url: url })}
            testId="agm-pdf-upload"
          />
        </SectionCard>

        <SectionCard
          title="Footer — BCS Annual Accounts"
          description="Audited annual accounts PDF in the site footer (independent of the public Accounts page, which can stay hidden)."
          onSave={() => saveSettingsSection("Footer — BCS Annual Accounts")}
          saving={isSaving("Footer — BCS Annual Accounts")}
          saveTestId="save-annual-accounts-pdf-btn"
        >
          <PdfUploadField
            label="Annual accounts PDF"
            value={settings.annual_accounts_pdf_url || ""}
            nameHint="bcs-annual-accounts"
            onChange={(url) => setSettings({ ...settings, annual_accounts_pdf_url: url })}
            testId="annual-accounts-pdf-upload"
          />
        </SectionCard>

        <div className="lg:col-span-2 space-y-6">
          <h2 className="font-serif text-2xl font-semibold text-ink pt-2">About page</h2>

          <SectionCard
            title="Our story — Hero"
            description="Top banner on the About page. Stats also appear on the homepage hero."
            onSave={() => saveAboutSection("Our story — Hero")}
            saving={isSaving("Our story — Hero")}
            saveTestId="save-about-hero-btn"
          >
            <Field label="Eyebrow" value={about.hero?.eyebrow} onChange={(v) => setAboutHero("eyebrow", v)} />
            <Field label="Title" value={about.hero?.title} onChange={(v) => setAboutHero("title", v)} />
            <Field label="Subtitle" value={about.hero?.subtitle} onChange={(v) => setAboutHero("subtitle", v)} />
            <Field label="Intro paragraph" value={about.hero?.intro} onChange={(v) => setAboutHero("intro", v)} textarea />
            <div className="space-y-3 pt-2">
              <p className="label-bcs">Stats</p>
              {(about.hero?.stats || []).map((stat, i) => (
                <ListItemCard key={i} index={i} onRemove={() => removeHeroStat(i)}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Label" value={stat.label} onChange={(v) => updateHeroStat(i, "label", v)} />
                    <Field label="Value" value={stat.value} onChange={(v) => updateHeroStat(i, "value", v)} />
                  </div>
                </ListItemCard>
              ))}
              <AddItemButton label="Add stat" onClick={addHeroStat} testId="about-add-stat" />
            </div>
          </SectionCard>

          <SectionCard
            title="Our vision"
            description="Quoted vision statement."
            onSave={() => saveAboutSection("Our vision")}
            saving={isSaving("Our vision")}
            saveTestId="save-about-vision-btn"
          >
            <Field label="Eyebrow" value={about.vision?.eyebrow} onChange={(v) => setAboutVision("eyebrow", v)} />
            <Field label="Vision quote" value={about.vision?.quote} onChange={(v) => setAboutVision("quote", v)} textarea />
          </SectionCard>

          <SectionCard
            title="Founded in Mumbai"
            description="History, membership and founder members."
            onSave={() => saveAboutSection("Founded in Mumbai")}
            saving={isSaving("Founded in Mumbai")}
            saveTestId="save-about-founded-btn"
          >
            <Field label="Section title" value={about.founded?.title} onChange={(v) => setAboutFounded("title", v)} />
            <Field label="Founding story" value={about.founded?.body} onChange={(v) => setAboutFounded("body", v)} textarea />
            <Field label="Membership today" value={about.founded?.membership} onChange={(v) => setAboutFounded("membership", v)} textarea />
            <Field
              label="Founder members heading"
              value={about.founded?.founder_members_heading}
              onChange={(v) => setAboutFounded("founder_members_heading", v)}
            />
            <div className="space-y-3">
              <p className="label-bcs">Founder members</p>
              {(about.founded?.founder_members || []).map((name, i) => (
                <ListItemCard key={i} index={i} onRemove={() => removeFounderMember(i)}>
                  <Field label="Name" value={name} onChange={(v) => updateFounderMember(i, v)} />
                </ListItemCard>
              ))}
              <AddItemButton label="Add founder member" onClick={addFounderMember} testId="about-add-founder" />
            </div>
            <Field label="Founder members note" value={about.founded?.founder_note} onChange={(v) => setAboutFounded("founder_note", v)} />
            <Field label="Registration line" value={about.founded?.registration} onChange={(v) => setAboutFounded("registration", v)} />
          </SectionCard>

          <SectionCard
            title="Milestones"
            description="Timeline of key moments in the Sabha's history."
            onSave={() => saveAboutSection("Milestones")}
            saving={isSaving("Milestones")}
            saveTestId="save-about-milestones-btn"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Section eyebrow"
                value={about.milestones_section?.eyebrow}
                onChange={(v) => setMilestonesSection("eyebrow", v)}
              />
              <Field
                label="Section title"
                value={about.milestones_section?.title}
                onChange={(v) => setMilestonesSection("title", v)}
              />
            </div>
            <div className="space-y-3 pt-2">
              {(about.milestones || []).map((m, i) => (
                <ListItemCard key={i} index={i} onRemove={() => removeListItem("milestones", i)}>
                  <Field label="Period" value={m.period} onChange={(v) => updateListItem("milestones", i, "period", v)} />
                  <Field label="Title" value={m.title} onChange={(v) => updateListItem("milestones", i, "title", v)} />
                  <Field label="Description" value={m.body} onChange={(v) => updateListItem("milestones", i, "body", v)} textarea />
                </ListItemCard>
              ))}
              <AddItemButton
                label="Add milestone"
                onClick={() => addListItem("milestones", { period: "", title: "", body: "" })}
                testId="about-add-milestone"
              />
            </div>
          </SectionCard>

          <SectionCard
            title="Today's work — Programmes"
            description="Current programmes and initiatives."
            onSave={() => saveAboutSection("Today's work — Programmes")}
            saving={isSaving("Today's work — Programmes")}
            saveTestId="save-about-programmes-btn"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Section eyebrow"
                value={about.programmes_section?.eyebrow}
                onChange={(v) => setProgrammesSection("eyebrow", v)}
              />
              <Field
                label="Section title"
                value={about.programmes_section?.title}
                onChange={(v) => setProgrammesSection("title", v)}
              />
            </div>
            <div className="space-y-3 pt-2">
              {(about.programmes || []).map((p, i) => (
                <ListItemCard key={i} index={i} onRemove={() => removeListItem("programmes", i)}>
                  <Field label="Programme name" value={p.title} onChange={(v) => updateListItem("programmes", i, "title", v)} />
                  <Field label="Description" value={p.body} onChange={(v) => updateListItem("programmes", i, "body", v)} textarea />
                </ListItemCard>
              ))}
              <AddItemButton
                label="Add programme"
                onClick={() => addListItem("programmes", { title: "", body: "" })}
                testId="about-add-programme"
              />
            </div>
          </SectionCard>

          <SectionCard
            title="With gratitude"
            description="Thank-you message before the membership CTA."
            onSave={() => saveAboutSection("With gratitude")}
            saving={isSaving("With gratitude")}
            saveTestId="save-about-gratitude-btn"
          >
            <Field label="Eyebrow" value={about.gratitude?.eyebrow} onChange={(v) => setAboutGratitude("eyebrow", v)} />
            <Field label="Section title" value={about.gratitude?.title} onChange={(v) => setAboutGratitude("title", v)} />
            <Field label="Main text" value={about.gratitude?.body} onChange={(v) => setAboutGratitude("body", v)} textarea />
            <Field label="Closing paragraph" value={about.gratitude?.closing} onChange={(v) => setAboutGratitude("closing", v)} textarea />
          </SectionCard>

          <SectionCard
            title="Membership CTA"
            description="Call-to-action banner on the About page."
            onSave={() => saveAboutSection("Membership CTA")}
            saving={isSaving("Membership CTA")}
            saveTestId="save-about-cta-btn"
          >
            <Field label="Title" value={about.cta?.title} onChange={(v) => setAboutCta("title", v)} />
            <Field label="Subtitle" value={about.cta?.subtitle} onChange={(v) => setAboutCta("subtitle", v)} />
            <Field label="Motto line" value={about.cta?.motto} onChange={(v) => setAboutCta("motto", v)} />
          </SectionCard>

          <SectionCard
            title="Former Presidents"
            description="Past presidents shown on the About page before the current office bearers. Consecutive terms for the same person are grouped automatically on the public site."
            onSave={() => saveAboutSection("Former Presidents")}
            saving={isSaving("Former Presidents")}
            saveTestId="save-about-former-presidents-btn"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Section eyebrow"
                value={about.former_presidents_section?.eyebrow}
                onChange={(v) => setFormerPresidentsSection("eyebrow", v)}
              />
              <Field
                label="Section title"
                value={about.former_presidents_section?.title}
                onChange={(v) => setFormerPresidentsSection("title", v)}
              />
            </div>
            <Field
              label="Section subtitle"
              value={about.former_presidents_section?.subtitle}
              onChange={(v) => setFormerPresidentsSection("subtitle", v)}
              textarea
            />
            <div className="space-y-3 pt-2">
              {(about.former_presidents || []).map((p, i) => (
                <ListItemCard key={i} index={i} onRemove={() => removeListItem("former_presidents", i)}>
                  <Field
                    label="Term (years)"
                    value={p.period}
                    onChange={(v) => updateListItem("former_presidents", i, "period", v)}
                  />
                  <Field
                    label="President name"
                    value={p.name}
                    onChange={(v) => updateListItem("former_presidents", i, "name", v)}
                  />
                  <Field
                    label="Parish"
                    value={p.parish}
                    onChange={(v) => updateListItem("former_presidents", i, "parish", v)}
                  />
                </ListItemCard>
              ))}
              <AddItemButton
                label="Add former president"
                onClick={() => addListItem("former_presidents", { period: "", name: "", parish: "" })}
                testId="about-add-former-president"
              />
            </div>
          </SectionCard>

          <SectionCard
            title="Leadership — Office bearers"
            description="Current elected leadership shown at the bottom of the About page. Use the arrows on each item to change display order, then save."
            onSave={() => saveAboutSection("Leadership — Office bearers")}
            saving={isSaving("Leadership — Office bearers")}
            saveTestId="save-about-leadership-btn"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Section eyebrow"
                value={about.leadership_section?.eyebrow}
                onChange={(v) => setLeadershipSection("eyebrow", v)}
              />
              <Field
                label="Section title"
                value={about.leadership_section?.title}
                onChange={(v) => setLeadershipSection("title", v)}
              />
            </div>
            <Field
              label="Section subtitle"
              value={about.leadership_section?.subtitle}
              onChange={(v) => setLeadershipSection("subtitle", v)}
              textarea
            />
            <div className="space-y-3 pt-2">
              {(about.leadership || []).map((l, i) => (
                <ListItemCard
                  key={i}
                  index={i}
                  onRemove={() => removeListItem("leadership", i)}
                  onMoveUp={() => moveListItem("leadership", i, -1)}
                  onMoveDown={() => moveListItem("leadership", i, 1)}
                  canMoveUp={i > 0}
                  canMoveDown={i < (about.leadership?.length || 0) - 1}
                  reorderTestId="about-leadership-move"
                >
                  <ImageUploadField
                    label="Photo"
                    value={l.image_url}
                    nameHint={l.name || l.role || `bearer-${i + 1}`}
                    onChange={(v) => updateListItem("leadership", i, "image_url", v)}
                    testId={`about-leadership-photo-${i}`}
                  />
                  <Field label="Role" value={l.role} onChange={(v) => updateListItem("leadership", i, "role", v)} />
                  <Field label="Name" value={l.name} onChange={(v) => updateListItem("leadership", i, "name", v)} />
                  <Field label="Parish / unit" value={l.note} onChange={(v) => updateListItem("leadership", i, "note", v)} />
                </ListItemCard>
              ))}
              <AddItemButton
                label="Add office bearer"
                onClick={() => addListItem("leadership", { role: "", name: "", note: "", image_url: "" })}
                testId="about-add-leadership"
              />
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
